import React from "react";
import { FilterSelect } from "./FilterSelect";
import { DateFilterInput } from "./DateFilterInput";
import { DataTable } from "./DataTable";
import { OperationBadge } from "./OperationBadge";
import { StatusBadge } from "./StatusBadge";
import { ActionDropdown } from "./ActionDropdown";
import { Pagination } from "./Pagination";
import { BsEye } from "react-icons/bs";
import { LuRefreshCw } from "react-icons/lu";
import { MdOutlineLocalShipping } from "react-icons/md";
import { BiCheckCircle } from "react-icons/bi";
import { IoClose } from "react-icons/io5";
import { formatDate } from "@/utils/date";
import { StockRequestStatusUpdate } from "@/services/stockSummaryService";
import { StockSummaryRow } from "@/types";

const StockSummaryFilterOpions = ({
  operationFilter,
  setOperationFilter,
  cityFilter,
  setCityFilter,
  statusFilter,
  setStatusFilter,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  filteredRequests,
  tableLoading,
  totalPages,
  setPaginationMeta,
  operationOptions,
  cityOptions,
  statusOptions,
  setSelectedRequest,
  isProcessedRequest,
  isProcessingRequest,
  openConfirmationModal,
  isCurrentAction,
  formatPriceUnit,
  paginationMeta,
}: {
  operationFilter: string;
  setOperationFilter: (value: string) => void;
  cityFilter: string;
  setCityFilter: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  startDate: string;
  setStartDate: (value: string) => void;
  endDate: string;
  setEndDate: (value: string) => void;
  filteredRequests: StockSummaryRow[];
  tableLoading: boolean;
  totalPages: number;
  setPaginationMeta:  React.Dispatch<React.SetStateAction<{
    total: number;
    page: number;
    limit: number;
}>>;
  operationOptions: string[];
  cityOptions: string[];
  statusOptions: string[];
  setSelectedRequest:React.Dispatch<React.SetStateAction<StockSummaryRow | null>>;
  isProcessedRequest: (request: StockSummaryRow) => boolean;
  isProcessingRequest: (request: StockSummaryRow) => boolean;
  openConfirmationModal: (
    requestId: string,
    status: StockRequestStatusUpdate,
    sourceType: string,
  ) => void;
  isCurrentAction: (requestId: string) => boolean;
  formatPriceUnit: (unit: string) => string;
  paginationMeta: {
    page: number;
    limit: number;
    total: number;
  };
}) => {
  return (
    <div className="rounded-2xl p-4 shadow-sm md:p-0">
      <div className="flex items-center justify-between rounded-t-2xl bg-white p-3">
        <h2 className="text-lg font-bold text-[#0f1f4b]">Product Requests</h2>
      </div>

      <div className="flex flex-col gap-4 border-b border-gray-100 bg-white px-4 py-4 md:flex-row md:flex-wrap md:items-end">
        <div className="self-center text-[11px] font-bold uppercase tracking-[0.26em] text-black md:mr-auto">
          Filters
        </div>

        <FilterSelect
          label="Operation"
          value={operationFilter}
          options={operationOptions}
          onChange={setOperationFilter}
        />
        <FilterSelect
          label="City"
          value={cityFilter}
          options={cityOptions}
          onChange={setCityFilter}
        />
        <FilterSelect
          label="Status"
          value={statusFilter}
          options={statusOptions}
          onChange={setStatusFilter}
        />

        <DateFilterInput
          label="Start Date"
          value={startDate}
          max={endDate || undefined}
          onChange={setStartDate}
        />

        <DateFilterInput
          label="End Date"
          value={endDate}
          min={startDate || undefined}
          onChange={setEndDate}
        />

        <button
          type="button"
          onClick={() => {
            setOperationFilter("All");
            setCityFilter("All");
            setStatusFilter("All");
            setStartDate("");
            setEndDate("");
          }}
          disabled={
            operationFilter === "All" &&
            cityFilter === "All" &&
            statusFilter === "All" &&
            !startDate &&
            !endDate
          }
          className="cursor-pointer rounded-xl border border-blue-500 bg-blue-500 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:border-blue-600 hover:bg-blue-600 disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-200 disabled:text-slate-400 disabled:opacity-40"
        >
          Reset
        </button>
      </div>

      <DataTable
        loading={tableLoading}
        isEmpty={!tableLoading && filteredRequests.length === 0}
        emptyMessage="No product requests available right now."
        loadingMessage="Loading product requests..."
        columns={[
          { key: "userName", label: "USER NAME" },
          { key: "operation", label: "OPERATION" },
          { key: "product", label: "PRODUCT" },
          { key: "quantity", label: "QUANTITY" },
          { key: "amount", label: "AMOUNT" },
          { key: "city", label: "CITY" },
          { key: "date", label: "DATE" },
          { key: "status", label: "STATUS" },
          { key: "action", label: "ACTION" },
        ]}
      >
        {filteredRequests.map((request) => {
          const actionItems = [
            {
              label: "View",
              icon: <BsEye size={15} />,
              onClick: () => setSelectedRequest(request),
              tone: "default" as const,
            },
            ...(!isProcessedRequest(request) &&
            !isProcessingRequest(request) &&
            request.status !== "Dispatch"
              ? [
                  {
                    label: "Process",
                    icon: <LuRefreshCw size={15} />,
                    onClick: () =>
                      openConfirmationModal(
                        request.id,
                        "PROCESS",
                        request.sourceType,
                      ),
                    disabled: isCurrentAction(request.id),
                    tone: "primary" as const,
                  },
                ]
              : []),
            ...(!isProcessedRequest(request) && request.status !== "Dispatch"
              ? [
                  {
                    label: "Dispatch",
                    icon: <MdOutlineLocalShipping size={16} />,
                    onClick: () =>
                      openConfirmationModal(
                        request.id,
                        "DISPATCH",
                        request.sourceType,
                      ),
                    disabled: isCurrentAction(request.id),
                    tone: "primary" as const,
                  },
                ]
              : []),
            ...(!isProcessedRequest(request)
              ? [
                  {
                    label: "Complete",
                    icon: <BiCheckCircle size={16} />,
                    onClick: () =>
                      openConfirmationModal(
                        request.id,
                        "COMPLETE",
                        request.sourceType,
                      ),
                    disabled: isCurrentAction(request.id),
                    tone: "success" as const,
                  },
                  {
                    label: "Cancel",
                    icon: <IoClose size={16} />,
                    onClick: () =>
                      openConfirmationModal(
                        request.id,
                        "CANCELLED",
                        request.sourceType,
                      ),
                    disabled: isCurrentAction(request.id),
                    tone: "danger" as const,
                  },
                ]
              : []),
          ];

          return (
            <tr
              key={request.id}
              className="transition-colors hover:bg-slate-50/80"
            >
              <td className="px-4 py-4 text-sm text-gray-700">
                {request.userName || "--"}
              </td>
              <td className="px-4 py-4 text-sm">
                <OperationBadge type={request.operation} />
              </td>
              <td className="px-4 py-4 text-sm text-gray-700">
                {request.product || "--"}
              </td>
              <td className="px-4 py-4 text-sm text-gray-700">
                {request.quantity
                  ? `${request.quantity}${
                      request.details.priceUnit
                        ? " " + formatPriceUnit(request.details.priceUnit)
                        : ""
                    }`
                  : "--"}
              </td>
              <td className="px-4 py-4 text-sm text-gray-700">
                {request.amount || "--"}
              </td>
              <td className="px-4 py-4 text-sm text-gray-700">
                {request.city || "--"}
              </td>
              <td className="px-4 py-4 text-sm text-gray-700">
                {request.createdAt ? formatDate(request.createdAt) : "--"}
              </td>
              <td className="px-4 py-4 text-sm">
                <StatusBadge status={request.status} />
              </td>
              <td className="px-4 py-4 text-sm">
                <ActionDropdown items={actionItems} />
              </td>
            </tr>
          );
        })}
      </DataTable>

      <Pagination
        total={paginationMeta.total}
        perPage={paginationMeta.limit}
        current={paginationMeta.page}
        totalPages={totalPages}
        onPageChange={(page) =>
          setPaginationMeta(
            (prev: { total: number; page: number; limit: number }) => ({
              ...prev,
              page,
            }),
          )
        }
        onPerPageChange={(limit) =>
          setPaginationMeta(
            (prev: { total: number; page: number; limit: number }) => ({
              ...prev,
              page: 1,
              limit,
            }),
          )
        }
      />
    </div>
  );
};

export default StockSummaryFilterOpions;
